import { IconPillTag } from "../../../../Atoms";
import { ITransferenciaValidacaoDTO } from "../../../../../types/loteAgendamento";

interface IPlanilhaValidationTableProps {
  fileName: string;
  resultados: ITransferenciaValidacaoDTO[];
  identificador: "placa" | "chassi";
  onRemover: () => void;
}

const StatusBadge = ({ aprovado }: { aprovado: boolean }) => {
  const cor = aprovado ? "#38b449" : "#e42e30";
  const icone = aprovado ? (
    <svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.83317 3.31246L0.958171 2.43746L0.666504 2.72913L1.83317 3.89579L4.33317 1.39579L4.0415 1.10413L1.83317 3.31246Z" fill={cor} />
    </svg>
  ) : (
    <svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.95817 1.33538L3.66442 1.04163L2.49984 2.20621L1.33525 1.04163L1.0415 1.33538L2.20609 2.49996L1.0415 3.66454L1.33525 3.95829L2.49984 2.79371L3.66442 3.95829L3.95817 3.66454L2.79359 2.49996L3.95817 1.33538Z" fill={cor} />
    </svg>
  );

  return (
    <IconPillTag color={cor} icon={icone}>
      {aprovado ? "Pronta" : "Reprovada"}
    </IconPillTag>
  );
};

/**
 * Per-row spreadsheet validation results, built specifically for the batch
 * import page — kept out of Atoms since it has no other consumer. See
 * `src/components/Molecules/Table` (styled-components/V1) for the generic
 * table primitives checked before building this: they don't model per-row
 * error expansion or status pills, so they weren't a fit here.
 */
export const PlanilhaValidationTable = ({
  fileName,
  resultados,
  identificador,
  onRemover,
}: IPlanilhaValidationTableProps) => {
  const validas = resultados.filter((r) => r.status === "APROVADO").length;
  const invalidas = resultados.length - validas;

  return (
    <div className="flex flex-col gap-6 overflow-hidden rounded-3xl border border-solid border-[#e5e5ea] bg-white shadow-[0_8px_24px_0_rgba(0,0,0,0.078)]">
      <div className="flex flex-col gap-4 p-8 pb-0 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              d="M7 22C6.45 22 5.97917 21.8042 5.5875 21.4125C5.19583 21.0208 5 20.55 5 20V4C5 3.45 5.19583 2.97917 5.5875 2.5875C5.97917 2.19583 6.45 2 7 2H13.175C13.4417 2 13.6958 2.05 13.9375 2.15C14.1792 2.25 14.3917 2.39167 14.575 2.575L18.425 6.425C18.6083 6.60833 18.75 6.82083 18.85 7.0625C18.95 7.30417 19 7.55833 19 7.825V20C19 20.55 18.8042 21.0208 18.4125 21.4125C18.0208 21.8042 17.55 22 17 22H7ZM11 4H7V20H17V10H14C13.1667 10 12.4583 9.70833 11.875 9.125C11.2917 8.54167 11 7.83333 11 7V4ZM13 4V7C13 7.28333 13.0958 7.52083 13.2875 7.7125C13.4792 7.90417 13.7167 8 14 8H17V7.825L13.175 4H13ZM10 19C9.71667 19 9.47917 18.9042 9.2875 18.7125C9.09583 18.5208 9 18.2833 9 18C9 17.7167 9.09583 17.4792 9.2875 17.2875C9.47917 17.0958 9.71667 17 10 17H12C12.2833 17 12.5208 17.0958 12.7125 17.2875C12.9042 17.4792 13 17.7167 13 18C13 18.2833 12.9042 18.5208 12.7125 18.7125C12.5208 18.9042 12.2833 19 12 19H10ZM10 15C9.71667 15 9.47917 14.9042 9.2875 14.7125C9.09583 14.5208 9 14.2833 9 14C9 13.7167 9.09583 13.4792 9.2875 13.2875C9.47917 13.0958 9.71667 13 10 13H14C14.2833 13 14.5208 13.0958 14.7125 13.2875C14.9042 13.4792 15 13.7167 15 14C15 14.2833 14.9042 14.5208 14.7125 14.7125C14.5208 14.9042 14.2833 15 14 15H10Z"
              fill="#20332F"
            />
          </svg>
          <div>
            <p className="font-['Mulish'] text-base font-bold text-[#20332f]">
              {fileName}
            </p>
            <p className="font-['Mulish'] text-[10px] text-[#666]">
              {resultados.length} linhas lidas
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <span className="rounded-full border border-solid border-[#38b449] bg-[#38b449]/20 px-3 py-1 font-['Inter'] text-[10px] text-[#38b449]">
            {validas} válidas
          </span>
          {invalidas > 0 && (
            <span className="rounded-full border border-solid border-[#e42e30] bg-[#e42e30]/20 px-3 py-1 font-['Inter'] text-[10px] text-[#e42e30]">
              {invalidas} inválida{invalidas > 1 ? "s" : ""}
            </span>
          )}
          <button
            type="button"
            onClick={onRemover}
            className="flex items-center gap-1.5 font-['Inter'] text-[10px] text-[#666]"
          >
            <svg
              width="7"
              height="8"
              viewBox="0 0 7 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.25 7.5C1.02083 7.5 0.824653 7.4184 0.661458 7.25521C0.498264 7.09201 0.416667 6.89583 0.416667 6.66667V1.25H0V0.416667H2.08333V0H4.58333V0.416667H6.66667V1.25H6.25V6.66667C6.25 6.89583 6.1684 7.09201 6.00521 7.25521C5.84201 7.4184 5.64583 7.5 5.41667 7.5H1.25ZM5.41667 1.25H1.25V6.66667H5.41667V1.25ZM2.08333 5.83333H2.91667V2.08333H2.08333V5.83333ZM3.75 5.83333H4.58333V2.08333H3.75V5.83333Z"
                fill="#666666"
              />
            </svg>
            Remover
          </button>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-[40px_1fr_1.5fr_1.5fr_1fr] gap-2.5 border-0 border-b border-solid border-b-[#e5e5ea] bg-[#666666]/10 p-[15px] font-['Mulish'] text-base font-bold leading-[20px] text-[#20332f]">
          <span>#</span>
          <span>Identificador</span>
          <span>Cliente</span>
          <span>Contato</span>
          <span>Status</span>
        </div>

        <div className="max-h-[325px] overflow-y-auto">
          {resultados.map((resultado, index) => {
            const aprovado = resultado.status === "APROVADO";

            return (
              <div
                key={index}
                className="border-0 border-b border-solid border-b-[#e5e5ea] p-[15px] last:border-b-0"
              >
                <div className="grid grid-cols-[40px_1fr_1.5fr_1.5fr_1fr] items-center gap-2.5 font-['Mulish'] text-base font-bold leading-[20px] text-[#20332f]">
                  <span>{index + 1}</span>
                  <span>{resultado.veiculo[identificador]}</span>
                  <div>
                    <p>{resultado.cliente.nome}</p>
                    <p className="text-xs font-normal leading-[15px]">
                      {resultado.cliente.cpfCnpj}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-normal leading-[15px]">
                      {resultado.cliente.telefone || "-"}
                    </p>
                    <p className="text-xs font-normal leading-[15px]">
                      {resultado.cliente.email || "-"}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <StatusBadge aprovado={aprovado} />
                  </div>
                </div>

                {!aprovado && resultado.erros.length > 0 && (
                  <p className="mt-2 rounded-[10px] bg-[#e42e30] px-5 py-1.5 font-['Mulish'] text-base font-bold text-white">
                    {resultado.erros.join(" ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
